import { useLayoutEffect, useRef, useState, type ComponentType, type ReactNode, type SVGProps } from "react";
import {
  Button as AriaButton,
  Menu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Separator,
  Header,
  type Key,
} from "react-aria-components";
import { CalendarDays, Check, ChevronDown, ChevronLeft, Ellipsis, Eye, MessageSquare, PencilLine, Printer, Trash2, Undo2 } from "lucide-react";
import { cn } from "@/utils/helpers";
import { KitPopover } from "../../components/kit/Popover";
import { KitModal } from "../../components/kit/Modal";
import type { PostViewport } from "./StatusBanner";
import "./postPage.css";

/**
 * "publishing" is the Live tab (the team's numbers live there too; an old
 * `?phase=analytics` link opens it).
 */
export type PostPhase = "concept" | "brief" | "upload" | "publishing" | "distribution";

export interface BriefVersionOption {
  version: number;
  /** Left out where the API has no date for a version (the client's briefs). */
  createdAt?: string;
  /** Left out on the client's page, where every version they can read was sent. */
  sent?: boolean;
}

const MENU_ITEM =
  "flex min-h-[34px] cursor-pointer items-center gap-2.5 rounded-row px-2.5 text-[13px] text-co-text outline-none data-[focused]:bg-co-hover data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40";

function versionDate(iso: string | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function versionLine(option: BriefVersionOption, current: number): string {
  const parts = [`Version ${option.version}`, versionDate(option.createdAt)];
  if (option.sent) parts.push("sent");
  if (option.version === current) parts.push("current");
  return parts.filter(Boolean).join(" · ");
}

/** Icon button / Ghost on the control glass: a 30px circle, the ellipsis 15 in muted. */
const ICON_BUTTON =
  "co-glass-control grid size-[30px] shrink-0 place-items-center rounded-pill text-co-muted outline-none transition-colors hover:text-co-text data-[focus-visible]:outline-2 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-co-accent";

/**
 * PostHeader (DESIGN.md "The Calendar in Parts"): the post title, Version ▾ once
 * a brief has more than one version, the ⋯ menu (Move to another day, Delete
 * post), and the Concept and Creative brief tabs with their approved checks. No
 * primary and no stage track; the date rail and the banner carry those. On the
 * desk Comments sit in the rail; a tablet gets a Comments button and a phone a
 * Comments item in the ⋯. Tablet folds Version into the ⋯; the phone adds a top bar.
 */
export default function PostHeader({
  title,
  viewport,
  phase,
  onPhaseChange,
  conceptApproved,
  briefApproved,
  briefExists,
  showDistribution,
  hideConcept = false,
  versions,
  currentVersion,
  viewingVersion,
  onPickVersion,
  onMove,
  onDelete,
  deleteBlocked,
  onPrint,
  onPreviewAsClient,
  previewLabel,
  onEditIdea,
  onEditBrief,
  ideaActions = [],
  printBusy,
  onRecall,
  recallLabel,
  recallBusy,
  phoneTop,
  comments,
  briefTab = "disabled",
  versionsLabel = "Creative brief versions",
  pageActions,
  tag,
  inset = false,
}: {
  title: string;
  viewport: PostViewport;
  phase: PostPhase;
  onPhaseChange: (phase: PostPhase) => void;
  conceptApproved: boolean;
  briefApproved: boolean;
  briefExists: boolean;
  showDistribution: boolean;
  /** A quick post has no concept and no brief, so both tabs stay off the page. */
  hideConcept?: boolean;
  /** Brief versions, newest first. Version ▾ shows only when there is more than one. */
  versions: BriefVersionOption[];
  currentVersion: number | null;
  /** A picked older version being read, or null for the live brief. */
  viewingVersion: number | null;
  onPickVersion: (version: number | null) => void;
  onMove?: () => void;
  onDelete?: () => void;
  deleteBlocked?: boolean;
  /** Print or save the creative brief as a PDF (the existing export). */
  onPrint?: () => void;
  /** The post as the client will see it. */
  onPreviewAsClient?: () => void;
  /** "Preview as Fatafeat" (Figma 08 ⋯ menu). */
  previewLabel?: string;
  /** The picked idea's words, before it goes to the client. */
  onEditIdea?: () => void;
  /** The creative brief's hand edit, while the brief can change. */
  onEditBrief?: () => void;
  /** The ideas page's moves on the idea on the slide (Redirect in the chat, a new angle, its words, its media). */
  ideaActions?: Array<{ id: string; label: string; onPress: () => void; disabled?: boolean }>;
  printBusy?: boolean;
  /** Take the brief back off the client's page; only while it is with them. */
  onRecall?: () => void;
  recallLabel?: string;
  recallBusy?: boolean;
  /** Phone only: the top bar's back label, the post's date and the meta line. */
  phoneTop?: { monthLabel: string; dateLabel: string; meta: string; onBack: () => void };
  /**
   * The team's comments where the rail cannot sit beside the page: a Comments
   * button with its count on a tablet, a Comments item in the phone's ⋯.
   * The desk has none; its rail carries Comments.
   */
  comments?: { count: number; onOpen: () => void };
  /** The client's page hides the Creative brief tab until a brief was sent to them. */
  briefTab?: "disabled" | "hidden";
  /** What the versions are, for the menu's name and heading. */
  versionsLabel?: string;
  /** The page's own actions beside the title on desktop and tablet (the ideas pages' Write your own and Pick). The phone keeps its bottom bar. */
  pageActions?: ReactNode;
  /** A tag before the header's actions (the client's Approved); on a phone it sits under the meta. */
  tag?: ReactNode;
  /**
   * The header pads itself to the Pen's gutters and reaches out of a column
   * padded the same (desktop 32, tablet 24), so its rule runs edge to edge.
   */
  inset?: boolean;
}) {
  const phone = viewport === "phone";
  const tablet = viewport === "tablet";
  const hasVersions = versions.length > 1 && currentVersion !== null;
  const [sheetOpen, setSheetOpen] = useState(false);
  const versionsInMenu = hasVersions && viewport !== "desktop";

  type Action = { id: string; label: string; icon: ComponentType<SVGProps<SVGSVGElement> & { strokeWidth?: number }>; danger?: boolean; disabled?: boolean };
  const actions: Action[] = [
    // Figma 08 ⋯ menu order: Preview as X · Print or save as PDF · Pull back from X · Move to another day,
    // then our own moves (the idea's words and media, the brief), comments on the phone, and Delete post last.
    ...(onPreviewAsClient ? [{ id: "preview", label: previewLabel ?? "Preview as client", icon: Eye }] : []),
    ...(onPrint ? [{ id: "print", label: "Print or save as PDF", icon: Printer, disabled: printBusy }] : []),
    ...(onRecall ? [{ id: "recall", label: recallLabel ?? "Recall from the client", icon: Undo2, disabled: recallBusy }] : []),
    ...(onMove ? [{ id: "move", label: "Move to another day", icon: CalendarDays }] : []),
    ...(onEditIdea ? [{ id: "edit-idea", label: "Edit the idea", icon: PencilLine }] : []),
    ...(onEditBrief ? [{ id: "edit-brief", label: "Edit the brief", icon: PencilLine }] : []),
    ...ideaActions.map((action) => ({ id: `idea-${action.id}`, label: action.label, icon: PencilLine, disabled: action.disabled })),
    ...(phone && comments ? [{ id: "comments", label: comments.count > 0 ? `Comments · ${comments.count}` : "Comments", icon: MessageSquare }] : []),
    ...(onDelete
      ? [{
          id: "delete",
          label: deleteBlocked ? "Delete post (unavailable while it is working)" : "Delete post",
          icon: Trash2,
          danger: true,
          disabled: deleteBlocked,
        }]
      : []),
  ];
  const hasMenu = actions.length > 0 || versionsInMenu;

  const runMenu = (key: Key) => {
    const id = String(key);
    if (id.startsWith("idea-")) {
      ideaActions.find((action) => `idea-${action.id}` === id)?.onPress();
      return;
    }
    if (id === "comments") comments?.onOpen();
    else if (id === "move") onMove?.();
    else if (id === "delete") onDelete?.();
    else if (id === "print") onPrint?.();
    else if (id === "preview") onPreviewAsClient?.();
    else if (id === "edit-idea") onEditIdea?.();
    else if (id === "edit-brief") onEditBrief?.();
    else if (id === "recall") onRecall?.();
    else if (id.startsWith("version-")) {
      const version = Number(id.slice("version-".length));
      onPickVersion(version === currentVersion ? null : version);
    }
  };

  const trigger = (
    <AriaButton
      aria-label="More for this post"
      className={ICON_BUTTON}
      onPress={phone ? () => setSheetOpen(true) : undefined}
    >
      <Ellipsis className="size-[15px]" strokeWidth={2} aria-hidden="true" />
    </AriaButton>
  );

  // Phone: the same choices as a bottom sheet (DESIGN.md "Every post can be
  // deleted"), floating 8px in with its four pegs, over the scrim. The modal
  // traps focus and closes on Esc or a tap on the scrim.
  const sheet = phone && hasMenu ? (
    <>
      {trigger}
      <KitModal
        isOpen={sheetOpen}
        onOpenChange={setSheetOpen}
        aria-label="More for this post"
        hideCloseButton
        overlayClassName="co-post-sheet-overlay"
        modalClassName="co-post-sheet"
      >
        <span className="co-post-sheet-grabber" aria-hidden="true" />
        <div className="flex flex-col">
          {versionsInMenu ? (
            <>
              <p className="px-4 pb-1 pt-2 text-[12.5px] text-co-muted">{versionsLabel}</p>
              {versions.map((option) => (
                <button
                  key={option.version}
                  type="button"
                  className="co-post-sheet-row"
                  onClick={() => {
                    setSheetOpen(false);
                    runMenu(`version-${option.version}`);
                  }}
                >
                  <span className="min-w-0 flex-1 truncate">{versionLine(option, currentVersion ?? 0)}</span>
                  {(viewingVersion ?? currentVersion) === option.version ? (
                    <Check className="size-4 text-co-text2" strokeWidth={2} aria-hidden="true" />
                  ) : null}
                </button>
              ))}
              {actions.length ? <span className="mx-4 my-1 h-px bg-co-line" aria-hidden="true" /> : null}
            </>
          ) : null}
          {actions.map((action) => (
            <button
              key={action.id}
              type="button"
              disabled={action.disabled}
              className={cn("co-post-sheet-row", action.danger && "text-co-alert-ink")}
              onClick={() => {
                setSheetOpen(false);
                runMenu(action.id);
              }}
            >
              <action.icon className="size-[18px]" strokeWidth={1.7} aria-hidden="true" />
              {action.label}
            </button>
          ))}
          <span className="mx-4 my-1 h-px bg-co-line" aria-hidden="true" />
          <button type="button" className="co-post-sheet-row justify-center font-semibold" onClick={() => setSheetOpen(false)}>
            Cancel
          </button>
        </div>
      </KitModal>
    </>
  ) : null;

  const menu = !hasMenu ? null : phone ? sheet : (
    <MenuTrigger>
      {trigger}
      <KitPopover placement="bottom end" className="w-[240px] p-1.5">
        <Menu aria-label="More for this post" className="flex flex-col outline-none" onAction={runMenu}>
          {versionsInMenu ? (
            <>
              <MenuSection>
                <Header className="px-2.5 pb-1 pt-1.5 text-[12px] text-co-muted">{versionsLabel}</Header>
                {versions.map((option) => (
                  <MenuItem
                    key={option.version}
                    id={`version-${option.version}`}
                    textValue={`Version ${option.version}`}
                    className={MENU_ITEM}
                  >
                    <span className="min-w-0 flex-1 truncate">{versionLine(option, currentVersion ?? 0)}</span>
                    {(viewingVersion ?? currentVersion) === option.version ? (
                      <Check className="size-3.5 text-co-text2" strokeWidth={2} aria-hidden="true" />
                    ) : null}
                  </MenuItem>
                ))}
              </MenuSection>
              {actions.length ? <Separator className="mx-1 my-1 h-px bg-co-line" /> : null}
            </>
          ) : null}
          {actions.map((action) => (
            <MenuItem
              key={action.id}
              id={action.id}
              textValue={action.label}
              isDisabled={action.disabled}
              className={cn(MENU_ITEM, action.danger && "text-co-alert-ink")}
            >
              <action.icon
                className={cn("size-3.5", !action.danger && "text-co-text2")}
                strokeWidth={1.8}
                aria-hidden="true"
              />
              {action.label}
            </MenuItem>
          ))}
        </Menu>
      </KitPopover>
    </MenuTrigger>
  );

  const versionPicker =
    hasVersions && viewport === "desktop" ? (
      <MenuTrigger>
        <AriaButton className="inline-flex h-8 shrink-0 items-center gap-1 rounded-tip px-2 text-[13px] text-co-text2 outline-none transition-colors hover:bg-co-hover hover:text-co-text data-[focus-visible]:outline-2 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-co-accent">
          Version {viewingVersion ?? currentVersion}
          <ChevronDown className="size-3.5" strokeWidth={2} aria-hidden="true" />
        </AriaButton>
        <KitPopover placement="bottom end" className="w-[260px] p-1.5">
          <Menu aria-label={versionsLabel} className="flex flex-col outline-none" onAction={runMenu}>
            {versions.map((option) => (
              <MenuItem
                key={option.version}
                id={`version-${option.version}`}
                textValue={`Version ${option.version}`}
                className={MENU_ITEM}
              >
                <span className="min-w-0 flex-1 truncate">{versionLine(option, currentVersion ?? 0)}</span>
                {(viewingVersion ?? currentVersion) === option.version ? (
                  <Check className="size-3.5 text-co-text2" strokeWidth={2} aria-hidden="true" />
                ) : null}
              </MenuItem>
            ))}
          </Menu>
        </KitPopover>
      </MenuTrigger>
    ) : null;

  const tabs = (
    <PostTabs
      phase={phase}
      onPhaseChange={onPhaseChange}
      conceptApproved={conceptApproved}
      briefApproved={briefApproved}
      briefExists={briefExists}
      showDistribution={showDistribution}
      hideConcept={hideConcept}
      hideBrief={hideConcept || (briefTab === "hidden" && !briefExists)}
      compact={phone}
    />
  );

  if (phone && phoneTop) {
    return (
      <header className="border-b border-co-line">
        <div className="flex h-12 items-center justify-between gap-2">
          <button
            type="button"
            onClick={phoneTop.onBack}
            aria-label={`Back to ${phoneTop.monthLabel}`}
            className="-ml-1.5 grid size-9 place-items-center text-co-text outline-none focus-visible:outline-2 focus-visible:outline-co-accent"
          >
            <ChevronLeft className="size-5" strokeWidth={2} aria-hidden="true" />
          </button>
          <p className="min-w-0 truncate text-[15px] font-semibold text-co-text">{phoneTop.dateLabel}</p>
          {menu ?? <span className="size-8" aria-hidden="true" />}
        </div>
        <h1 className="mt-2 line-clamp-2 font-section text-[24px] font-bold leading-[1.1] tracking-normal text-co-text">{title}</h1>
        <p className="mt-1.5 text-[12.5px] text-co-muted">{phoneTop.meta}</p>
        {tag ? <div className="mt-2">{tag}</div> : null}
        <div className="mt-2">{tabs}</div>
      </header>
    );
  }

  if (tablet) {
    // Post header · Tablet: the title over the tabs, the ⋯ pinned top right;
    // the page's own actions sit in a row under the rule, right-aligned.
    return (
      <>
        <header
          className={cn(
            "relative flex flex-col gap-2.5 pt-4 shadow-[inset_0_-1px_0_var(--co-line)]",
            inset ? "-mx-6 pl-6 pr-16" : "pr-12",
          )}
        >
          <h1 className="line-clamp-2 font-section text-[24px]/[1.1] font-bold tracking-normal text-co-text">{title}</h1>
          {tag ? <div>{tag}</div> : null}
          {tabs}
          <div className={cn("absolute top-[14px] flex items-center gap-1", inset ? "right-6" : "right-0")}>
            {comments ? (
              <AriaButton
                onPress={comments.onOpen}
                aria-label={comments.count > 0 ? `Comments, ${comments.count} open` : "Comments"}
                className="inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-pill px-2 text-[13px] text-co-text2 outline-none transition-colors hover:text-co-text data-[focus-visible]:outline-2 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-co-accent"
              >
                <MessageSquare className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
                {comments.count > 0 ? <span className="text-co-muted">{comments.count}</span> : null}
              </AriaButton>
            ) : null}
            {menu}
          </div>
        </header>
        {pageActions ? <div className="flex justify-end gap-2.5 px-2 pb-1 pt-4">{pageActions}</div> : null}
      </>
    );
  }

  return (
    <header className={cn("flex flex-col gap-3 pt-5 shadow-[inset_0_-1px_0_var(--co-line)]", inset && "-mx-8 px-8")}>
      <div className="flex items-start gap-3">
        <h1 className="min-w-0 flex-1 line-clamp-2 font-section text-[26px]/[1.1] font-bold tracking-normal text-co-text">
          {title}
        </h1>
        <div className="flex shrink-0 items-center gap-2.5">
          {tag}
          {pageActions}
          {versionPicker}
          {menu}
        </div>
      </div>
      {tabs}
    </header>
  );
}

function PostTabs({
  phase,
  onPhaseChange,
  conceptApproved,
  briefApproved,
  briefExists,
  showDistribution,
  hideConcept = false,
  hideBrief = false,
  compact = false,
}: {
  phase: PostPhase;
  onPhaseChange: (phase: PostPhase) => void;
  conceptApproved: boolean;
  briefApproved: boolean;
  briefExists: boolean;
  showDistribution: boolean;
  hideConcept?: boolean;
  hideBrief?: boolean;
  /** Phone: four tabs are wider than the screen, so the row scrolls sideways. */
  compact?: boolean;
}) {
  const navRef = useRef<HTMLElement>(null);
  const lineRef = useRef<HTMLSpanElement>(null);
  const active: PostPhase =
    phase === "brief" && !briefExists ? "concept" : phase === "upload" || phase === "distribution" ? "publishing" : phase;

  // The accent line slides to the chosen tab (200ms, ease-out-expo) and takes
  // its width. Written straight to the element: it is layout, not state.
  useLayoutEffect(() => {
    const nav = navRef.current;
    const line = lineRef.current;
    const tab = nav?.querySelector<HTMLElement>(`[data-phase="${active}"]`);
    if (!nav || !line || !tab) return;
    const place = () => {
      line.style.width = `${tab.offsetWidth}px`;
      line.style.transform = `translateX(${tab.offsetLeft}px)`;
    };
    place();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place);
    observer?.observe(nav);
    return () => observer?.disconnect();
  }, [active, conceptApproved, briefApproved, showDistribution, hideConcept, hideBrief]);

  const tab = (id: PostPhase, label: string, approved: boolean, disabledHint?: string): ReactNode => {
    const selected = active === id;
    const disabled = !!disabledHint;
    return (
      <button
        type="button"
        data-phase={id}
        aria-current={selected ? "page" : undefined}
        aria-disabled={disabled || undefined}
        title={disabledHint}
        onClick={() => {
          if (!disabled) onPhaseChange(id);
        }}
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5 pb-[9px] text-[14px] leading-[19px] outline-none transition-colors duration-[var(--duration-fast)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent",
          selected ? "font-semibold text-co-text" : "font-medium text-co-faint hover:text-co-text2",
          disabled && "cursor-not-allowed hover:text-co-faint",
        )}
      >
        {label}
        {approved ? (
          <Check className="size-3.5 text-co-text2" strokeWidth={2} aria-label="approved by the client" />
        ) : null}
      </button>
    );
  };

  return (
    <nav
      ref={navRef}
      aria-label="Post parts"
      className={cn("relative flex items-end", compact ? "no-scrollbar gap-4 overflow-x-auto" : "gap-[22px]")}
    >
      {hideConcept ? null : tab("concept", "Concept", conceptApproved)}
      {hideBrief ? null : tab("brief", "Creative brief", briefApproved, briefExists ? undefined : "Written after an idea is picked")}
      {showDistribution ? tab("publishing", "Live", false) : null}
      <span
        ref={lineRef}
        aria-hidden="true"
        className="co-post-tabline pointer-events-none absolute bottom-0 left-0 h-0.5 bg-co-accent"
      />
    </nav>
  );
}
